"use client"

import { motion, AnimatePresence } from "framer-motion"
import { useState } from "react"
import { Section } from "@/components/layout/Section"
import { Card } from "@/components/ui/card"
import { Code, Smartphone, ShoppingCart, Zap, Palette, Globe, Cpu, Bot, Monitor, Store, PenTool } from "lucide-react"
import Link from "next/link"
import { ArrowRight } from "lucide-react"
import { CodeAnimation } from "@/components/ui/CodeAnimation"
import { CTA } from "@/components/sections/CTA"
import { usePathname } from "next/navigation"
import { isValidLocale, type Locale } from "@/lib/i18n"
import { getTranslations } from "@/lib/translations"

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
}

export default function ServicesPage() {
  const pathname = usePathname()
  
  // Get current locale from pathname
  const getCurrentLocale = (): Locale => {
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    return isValidLocale(firstSegment) ? firstSegment : 'tr'
  }

  const currentLocale = getCurrentLocale()
  const t = getTranslations(currentLocale)

  const allServices = [
    {
      title: "Web Design",
      description: "Modern, user-focused and SEO-friendly web designs. We design professional websites that reflect your brand's digital identity and increase conversion rates.",
      icon: Palette,
      href: `/${currentLocale}/services/web-design`,
      code: "const design = createModernUI();",
    },
    {
      title: "Web Development",
      description: "Scalable web applications with Next.js, React and modern technologies. We develop high-performance, secure and easy-to-maintain software solutions.",
      icon: Code,
      href: `/${currentLocale}/services/web-development`,
      code: "function buildApp() { return <App />; }",
    },
    {
      title: "UI & UX Design",
      description: "User research, wireframes, prototypes and interface design. We design easy-to-use, conversion-focused digital experiences for your web and mobile products.",
      icon: PenTool,
      href: `/${currentLocale}/services/ui-ux-design`,
      code: "const flow = design.prototype(user);",
    },
    {
      title: "Mobile App",
      description: "Native and cross-platform mobile applications for iOS and Android. We offer high-performance mobile solutions that prioritize user experience.",
      icon: Smartphone,
      href: `/${currentLocale}/services/mobile-app`,
      code: "const app = new MobileApp();",
    },
    {
      title: "Desktop App",
      description: "Modern desktop applications for Windows, macOS and Linux. We develop platform-independent solutions with Electron and native technologies.",
      icon: Monitor,
      href: `/${currentLocale}/services/desktop-app`,
      code: "const desktop = new DesktopApp();",
    },
    {
      title: "E-Commerce Systems",
      description: "Shopify, WooCommerce and custom e-commerce solutions. We build user-friendly and secure online stores that increase your sales.",
      icon: ShoppingCart,
      href: `/${currentLocale}/services/e-commerce`,
      code: "shopify.init({ store: 'yours' });",
    },
    {
      title: "Shopify Design - Development",
      description: "Custom theme design and development for your Shopify store. SEO-optimized, mobile-friendly Shopify solutions that increase conversion rates.",
      icon: Store,
      href: `/${currentLocale}/services/shopify`,
      code: "shopify.theme.create({ modern: true });",
    },
    {
      title: "WooCommerce Design - Development",
      description: "Custom theme and plugin development for your WooCommerce store. WordPress-integrated, customizable and powerful e-commerce solutions.",
      icon: Store,
      href: `/${currentLocale}/services/woocommerce`,
      code: "woocommerce.init({ custom: true });",
    },
    {
      title: "Automation",
      description: "Smart solutions that automate your repetitive business processes. We connect your systems and build automations that save time and costs.",
      icon: Zap,
      href: `/${currentLocale}/services/automation`,
      code: "workflow.trigger('automation');",
    },
    {
      title: "AI Productized Services",
      description: "Photo, video and text generation with artificial intelligence technologies. We offer AI integrations and productized services tailored to your business needs.",
      icon: Bot,
      href: `/${currentLocale}/services/ai-services`,
      code: "ai.generate({ type: 'content' });",
    },
  ]

  return (
    <>
      <Section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-6">
            Our Services
          </h1>
          <p className="text-lg text-white/90 sm:text-xl">
            Comprehensive services that accompany you on your digital transformation journey
          </p>
        </div>
      </Section>

      <Section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {allServices.map((service) => {
              const Icon = service.icon
              const [isHovered, setIsHovered] = useState(false)
              
              return (
                <motion.div key={service.title} variants={itemVariants}>
                  <Link 
                    href={service.href} 
                    className="group block h-full"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <Card className="h-full p-6 lg:p-8 border border-slate-200 hover:border-slate-300 hover:shadow-xl transition-all duration-300 rounded-none">
                      {/* Icon / Code Animation Row */}
                      <div className="mb-6 min-h-[60px] flex items-start">
                        <AnimatePresence mode="wait">
                          {!isHovered ? (
                            <motion.div
                              key="icon"
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="inline-flex p-4 bg-slate-100 group-hover:bg-[rgb(3,74,166)] transition-colors duration-300"
                            >
                              <Icon className="h-6 w-6 text-slate-700 group-hover:text-white transition-colors duration-300" />
                            </motion.div>
                          ) : (
                            <motion.div
                              key="code"
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="w-full"
                            >
                              <CodeAnimation code={service.code} isVisible={isHovered} />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      
                      <h3 className="text-xl lg:text-2xl font-bold text-slate-900 mb-3 lg:mb-4 group-hover:text-[rgb(3,74,166)] transition-colors duration-300">
                        {service.title}
                      </h3>
                      
                      <p className="text-sm lg:text-base text-slate-600 leading-relaxed mb-4 lg:mb-6">
                        {service.description}
                      </p>
                      
                      <div className="flex items-center text-[rgb(3,74,166)] font-medium mt-auto pt-3 lg:pt-4 border-t border-slate-100 group-hover:border-slate-200 transition-colors">
                        <span className="text-xs lg:text-sm">Learn More</span>
                        <ArrowRight className="h-3 w-3 lg:h-4 lg:w-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </Section>
      <CTA locale={currentLocale} />
    </>
  )
}

